import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import gsap from 'gsap'

import { faqs } from '@/constants/content'

type FaqProps = {
    heading?: string
    subheading?: string
    showCta?: boolean
    /** The FAQ page opens every panel, the section previews five. */
    limit?: number
}

const DURATION = 0.45
const EASE = 'power2.inOut'

export const Faq: FC<FaqProps> = ({
    heading = 'Question? Answered',
    subheading = 'Have questions about NG Painting Pro? Browse our FAQ for answers.',
    showCta = true,
    limit = showCta ? 5 : undefined
}): ReactNode => {
    const [openIndex, setOpenIndex] = useState<number | null>(null)
    const panels = useRef<(HTMLDivElement | null)[]>([])
    const items = limit ? faqs.slice(0, limit) : faqs

    useEffect(() => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

        panels.current.forEach((panel, index) => {
            if (!panel) {
                return
            }

            const isOpen = index === openIndex
            const target = isOpen ? panel.scrollHeight : 0

            if (reduced) {
                gsap.set(panel, { height: isOpen ? 'auto' : 0 })

                return
            }

            gsap.to(panel, {
                height: target,
                duration: DURATION,
                ease: EASE,
                onComplete: () => {
                    if (isOpen) {
                        gsap.set(panel, { height: 'auto' })
                    }
                }
            })
        })
    }, [openIndex])

    return (
        <section className='faq-section padding-bottom'>
            <div className='w-layout-blockcontainer container w-container'>
                <div className='section-content-wrapper'>
                    <div className='section-title-wrapper align-center' data-reveal>
                        <h2 className='section-title'>
                            {heading}
                        </h2>

                        <p className='section-subtitle faq'>
                            {subheading}
                        </p>
                    </div>

                    <div className='faq-area'>
                        <div className='faq-wrapper' data-reveal-stagger='0.08'>
                            {items.map((faq, index) => {
                                const isOpen = index === openIndex

                                return (
                                    <div
                                        key={faq.question}
                                        className={isOpen ? 'single-faq is-open' : 'single-faq'}
                                    >
                                        <button
                                            type='button'
                                            className='faq-question-wrapper'
                                            aria-expanded={isOpen}
                                            aria-controls={`faq-panel-${index}`}
                                            onClick={() => setOpenIndex(isOpen ? null : index)}
                                        >
                                            <p className='faq-question'>
                                                {faq.question}
                                            </p>

                                            <div className='faq-icon-box'>
                                                <img
                                                    src='/images/angle-small-right-2-8.svg'
                                                    loading='lazy'
                                                    alt=''
                                                    className='faq-icon normal'
                                                />

                                                <img
                                                    src='/images/angle-small-right-2-8-1.svg'
                                                    loading='lazy'
                                                    alt=''
                                                    className='faq-icon active'
                                                />
                                            </div>
                                        </button>

                                        <div
                                            id={`faq-panel-${index}`}
                                            className='faq-answer-wrapper'
                                            ref={(node) => {
                                                panels.current[index] = node
                                            }}
                                        >
                                            <div className='faq-answer-inner'>
                                                <p className='faq-answer'>
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>

                        {showCta && (
                            <p className='faq-cta-area'>
                                {'Still have questions? '}

                                <Link href='/faqs' className='faq-cta-link'>
                                    Read all FAQs
                                </Link>
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}
